// The widget's entire stylesheet, injected once per page. Kept out of index.ts so the visual
// design is reviewable on its own.
//
// Two rules hold this together:
//
// 1. Every colour comes from a theme token (`--gp-*`, set per instance by applyThemeTokens
//    from the premise's stored theme). Where the design needs a tint or a hairline, it is
//    mixed out of a token with color-mix() rather than hardcoded, so a customer's own theme
//    still drives the whole surface. Each color-mix declaration is preceded by the plain
//    token as a fallback for browsers that don't support it.
// 2. Every size that has to scale is a variable, reassigned by the @container tiers at the
//    bottom. Nothing in between hardcodes a scaling size.

/**
 * The @font-face for the widget's typeface. Built at runtime rather than written into
 * WIDGET_CSS because a url() inside an injected <style> resolves against the host page, not
 * against embed.js — the caller passes the fonts directory derived from the script's own src.
 *
 * The family is given a private name instead of "Inter": this stylesheet lands in customer
 * pages, and a host page that declares its own Inter @font-face would otherwise collide with
 * ours in one direction or the other.
 */
export function fontFaceCss(fontsBaseUrl: URL): string {
  const src = new URL("inter-latin-wght-normal.woff2", fontsBaseUrl).toString();
  return `
@font-face {
  font-family: "IiGpInter";
  font-style: normal;
  font-weight: 100 900;
  font-display: swap;
  src: url("${src}") format("woff2");
  unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304, U+0308, U+0329, U+2000-206F, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD;
}
`;
}

export const WIDGET_CSS = `
.ii-gp-widget-host {
  /* The sizing container. max-width lives here rather than on the widget itself so the
     @container rules below measure the widget's own rendered width — put it on the widget
     and they would measure the host page's column instead, and a 420px widget in a 1200px
     column would style itself as if it were 1200px wide. */
  container-type: inline-size;
  max-width: var(--gp-max-width, 420px);
  /* Dropped into an unsized flex or grid item, the widget would otherwise be shrunk to its
     min-content width — narrow enough to clip the tab labels — because tab text is allowed
     to wrap. Floor it at a legible width, while still fitting a narrow phone. */
  min-width: min(260px, 100%);
}

.ii-gp-widget {
  /* Inter (self-hosted, see fontFaceCss) — the same face as the GymPlus dashboard. The
     system stack shows until it arrives, and stays if the font is blocked (strict CSP). */
  font-family: "IiGpInter", Inter, system-ui, -apple-system, "Segoe UI", sans-serif;

  /* Text-size step from the widget admin (app_settings.gp_widget.font_size, or the embed's
     data-font-size): every --gp-fs-* below, in every tier, is multiplied by it. Medium is 1;
     the modifier classes right after this rule set the other two. */
  --gp-font-scale: 1;

  /* Regular tier. The @container blocks at the bottom reassign these. */
  --gp-fs-tab: calc(13px * var(--gp-font-scale));
  --gp-sp-tab: 9px 8px;
  --gp-fs-total-count: calc(30px * var(--gp-font-scale));
  --gp-fs-total-label: calc(11px * var(--gp-font-scale));
  --gp-fs-box-count: calc(21px * var(--gp-font-scale));
  --gp-fs-box-label: calc(11px * var(--gp-font-scale));
  --gp-sp-box: 12px 10px;
  --gp-w-box-min: 130px;
  --gp-box-label-wrap: nowrap;
  --gp-fs-eyebrow: calc(10px * var(--gp-font-scale));
  --gp-h-logo: 14px;
  --gp-h-gauge: 72px;
  --gp-h-chart: 120px;
  --gp-fs-body: calc(12px * var(--gp-font-scale));
  --gp-fs-star: calc(18px * var(--gp-font-scale));
  --gp-sp-panel: 12px;
  --gp-h-figure: 72px;
  /* Forecast curve, coloured by level: empty → full. Semantic, not brand, so these are not
     theme tokens — but they are variables, so a theme or the host page can still override. */
  --gp-forecast-low: #2E9E5B;
  --gp-forecast-mid: #E3B23C;
  --gp-forecast-high: #D9483B;

  /* Radii follow the theme's thickness, but from a softer floor than the original 2px. */
  --gp-r-card: calc(var(--gp-border-radius, 2px) + 10px);
  --gp-r-ctl: calc(var(--gp-border-radius, 2px) + 6px);

  /* No outline: the card is defined by its shadow alone. */
  border-radius: var(--gp-r-card);
  background: var(--gp-realtime-bg, #fff);
  color: var(--gp-realtime-small-text, #333);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06), 0 12px 32px -12px rgba(0, 0, 0, 0.26);
  overflow: hidden;
  -webkit-font-smoothing: antialiased;
}
/* data-font="inherit": take the host page's font, so the widget reads as part of the site.
   A class rather than a custom property, because "inherit" through var() would apply the
   keyword to the custom property itself, not to font-family. */
.ii-gp-widget--inherit-font { font-family: inherit; }
.ii-gp-widget--font-small { --gp-font-scale: 0.9; }
.ii-gp-widget--font-large { --gp-font-scale: 1.15; }

.ii-gp-widget *, .ii-gp-widget *::before, .ii-gp-widget *::after { box-sizing: border-box; }
/* Host pages style bare elements (\`label { color: #fff }\`, \`form p { … }\`) and those rules
   beat inheritance, so a dark site's white labels would land on our white card. Pin every
   element back to inheriting from the card. :where() keeps this at one class of specificity,
   so it outranks element-only host rules while any of this sheet's own class rules below it
   still win by source order. */
.ii-gp-widget :where(div, p, span, label, legend, fieldset, form, h1, h2, h3, h4, h5, h6,
  a, strong, b, em, i, small, ul, ol, li, button, input, select, textarea) {
  color: inherit;
}
/* The hidden attribute must win over this sheet's own display rules (the AI-PT overlay and
   call-to-action are display:flex), or toggling it does nothing. */
.ii-gp-widget [hidden] { display: none !important; }

/* ── Tabs: a segmented control. The bar is tinted, the active tab is the card's own
      background lifted out of it — no colour fill, no weight change, the only cue is that
      slight difference in background. ── */

.ii-gp-widget__tabs {
  display: flex;
  gap: 4px;
  padding: 5px;
  background: var(--gp-tab-bg, #E7E7E7);
}
.ii-gp-widget__tab {
  flex: 1;
  padding: var(--gp-sp-tab);
  border: none;
  border-radius: var(--gp-r-ctl);
  background: none;
  font-family: inherit;
  font-size: var(--gp-fs-tab);
  font-weight: var(--gp-font-weight, 700);
  text-align: center;
  color: var(--gp-tab-text, #1780B5);
  cursor: pointer;
  /* Tab labels are full phrases ("create workout plan"), so they wrap rather than overflow
     in narrow embeds; flex stretch keeps all three the same height when one wraps. */
  line-height: 1.25;
  overflow-wrap: break-word;
  transition: background 0.15s ease, color 0.15s ease;
}
.ii-gp-widget__tab:hover:not(.ii-gp-widget__tab--active) {
  background: color-mix(in srgb, var(--gp-realtime-bg, #fff) 45%, transparent);
}
.ii-gp-widget__tab--active {
  background: var(--gp-realtime-bg, #fff);
}
.ii-gp-widget__tab--disabled { opacity: 0.5; cursor: not-allowed; }

/* The positioning root for the AI-PT overlay below: sized by .ii-gp-widget__main-content's
   normal flow height (tabs + panels + foot) since the overlay itself is absolutely positioned
   and so doesn't add to it. */
.ii-gp-widget__main { position: relative; }

/* Both tab panels occupy the same grid cell: the widget is as tall as the taller one on either
   tab, so switching never changes its height. */
.ii-gp-widget__panels { display: grid; }
.ii-gp-widget__panel { grid-area: 1 / 1; min-width: 0; display: flex; flex-direction: column; }
/* Both tabs share one cell, so the shorter one has spare height: the plot grows to fill it
   (the SVG stretches freely) rather than leaving a gap under the hours row. */
.ii-gp-widget__predictions, .ii-gp-widget__week-body, .ii-gp-widget__forecast, .ii-gp-widget__forecast-canvas {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  min-height: 0;
}
.ii-gp-widget__panel:not(.ii-gp-widget__panel--active) { visibility: hidden; pointer-events: none; }

/* ── Hero: the current count is the one number people came for, so it gets the size and
      the label steps back, instead of both sharing one 15px line ── */

.ii-gp-widget__total {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
  padding: 14px var(--gp-sp-panel);
  background: var(--gp-realtime-total-bg, #1780B5);
  color: var(--gp-realtime-total-text, #fff);
}
/* Closed: the message takes the place of the count and the category grid, so the opening
   time is the number the band leads with, the way the live count is when the gym is open. */
.ii-gp-widget__total--closed {
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  text-align: center;
}
.ii-gp-widget__closed-label {
  font-size: calc(var(--gp-fs-body) + 2px);
  font-weight: var(--gp-font-weight-bold, 700);
  line-height: 1.35;
}
/* With the counts gone the panel has height to spare; it goes to the band, not to the plot —
   the forecast keeps the size it has when the gym is open. */
.ii-gp-widget__now-head--closed { display: flex; flex: 1 1 auto; }
.ii-gp-widget__now-head--closed .ii-gp-widget__total { flex: 1 1 auto; }
.ii-gp-widget__panel:has(.ii-gp-widget__now-head--closed) .ii-gp-widget__predictions { flex: 0 0 auto; }
.ii-gp-widget__closed-time {
  font-size: calc(var(--gp-fs-total-count) * 1.15);
  font-weight: var(--gp-font-weight-bold, 700);
  line-height: 1.1;
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
}
.ii-gp-widget__total-count {
  font-size: var(--gp-fs-total-count);
  font-weight: var(--gp-font-weight-bold, 700);
  line-height: 1;
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
}
.ii-gp-widget__total-capacity {
  font-size: 0.5em;
  font-weight: var(--gp-font-weight, 700);
  opacity: 0.7;
  margin-left: 3px;
}
.ii-gp-widget__total-label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: var(--gp-fs-total-label);
  letter-spacing: 0.07em;
  text-transform: var(--gp-text-transform, uppercase);
  opacity: 0.85;
}
.ii-gp-widget__live-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: currentColor;
  flex-shrink: 0;
  animation: ii-gp-widget-pulse 1.8s ease-in-out infinite;
}
@keyframes ii-gp-widget-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.3; } }

/* ── Category boxes ── */

/* Flex rather than grid so a last row that does not divide evenly stretches to fill the
   width — an auto-fill grid leaves the leftover cells empty whenever the category count is
   not a multiple of the column count. */
.ii-gp-widget__boxes {
  display: flex;
  flex-wrap: wrap;
  /* No hairlines between boxes: the whitespace and centred columns do the separating. */
  background: var(--gp-realtime-bg, #fff);
}
/* border-box so --gp-w-box-min is the whole box, not its text area: otherwise a change to
   --gp-sp-box would quietly shift how many boxes fit per row. */
.ii-gp-widget__box {
  flex: 1 1 var(--gp-w-box-min);
  min-width: 0;
  background: var(--gp-realtime-bg, #fff);
  padding: var(--gp-sp-box);
  text-align: center;
}
/* Even splits by category count, so rows divide cleanly instead of leaving a lone box to
   stretch across the last row. Odd counts (5, 7) keep the width-driven basis and stretch. */
.ii-gp-widget__boxes--n1 .ii-gp-widget__box { flex-basis: 100%; }
.ii-gp-widget__boxes--n2 .ii-gp-widget__box,
.ii-gp-widget__boxes--n4 .ii-gp-widget__box { flex-basis: 50%; }
.ii-gp-widget__boxes--n3 .ii-gp-widget__box,
.ii-gp-widget__boxes--n6 .ii-gp-widget__box { flex-basis: 33.333%; }
@container (max-width: 319px) {
  .ii-gp-widget__boxes--n6 .ii-gp-widget__box { flex-basis: 50%; }
}
@container (min-width: 520px) {
  .ii-gp-widget__boxes--n4 .ii-gp-widget__box { flex-basis: 25%; }
}
/* Wide: the hero becomes a narrow column on the left (label over count) and the category
   boxes fill the rest of the same row. */
@container (min-width: 520px) {
  .ii-gp-widget__now-head { display: flex; align-items: stretch; }
  .ii-gp-widget__now-head .ii-gp-widget__total {
    flex: 0 0 auto;
    flex-direction: column;
    flex-wrap: nowrap;
    align-items: flex-start;
    justify-content: center;
    gap: 6px;
    min-width: 130px;
  }
  .ii-gp-widget__now-head .ii-gp-widget__boxes { flex: 1 1 auto; min-width: 0; }
}
.ii-gp-widget__box-count {
  font-size: var(--gp-fs-box-count);
  font-weight: var(--gp-font-weight-bold, 700);
  line-height: 1.1;
  letter-spacing: -0.01em;
  font-variant-numeric: tabular-nums;
  color: var(--gp-realtime-big-text, #003652);
}
/* Same treatment as the total's "/ N", a touch larger since the box count itself is smaller. */
.ii-gp-widget__box-capacity {
  font-size: 0.6em;
  font-weight: var(--gp-font-weight, 700);
  opacity: 0.7;
  margin-left: 2px;
}
/* Deliberately not uppercased, unlike the rest of the chrome: these are API-supplied
   category names, and Finnish compounds ("TOIMINNALLINEN") are markedly harder to read
   in caps at 11px, on top of being the labels most likely to need ellipsizing. */
.ii-gp-widget__box-label {
  margin-top: 5px;
  font-size: var(--gp-fs-box-label);
  line-height: 1.3;
  color: var(--gp-realtime-category-text, #666);
  white-space: var(--gp-box-label-wrap);
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ── Prediction gauges: the panel is a tint of the theme's prediction colour rather than
      the colour at full strength, which read as a solid slab of cyan ── */

.ii-gp-widget__predictions {
  padding: var(--gp-sp-panel);
  background: var(--gp-prediction-bg, #60C2E8);
  background: color-mix(in srgb, var(--gp-prediction-bg, #60C2E8) 22%, var(--gp-realtime-bg, #fff));
  border-top: 1px solid var(--gp-prediction-border, #1780B5);
  border-top-color: color-mix(in srgb, var(--gp-prediction-border, #1780B5) 22%, transparent);
}
/* The gauge-title token is a light colour that would be invisible on the tinted panel, so
   the text token is used for the title instead. */
.ii-gp-widget__predictions-title {
  margin: 0 0 10px;
  text-align: center;
  font-size: var(--gp-fs-body);
  font-weight: var(--gp-font-weight-bold, 700);
  color: var(--gp-prediction-gauge-percentage, #003652);
}
/* Forecast curve. The SVG stretches to the plot width (preserveAspectRatio none) while the
   axis and hour labels are HTML beside/below it, so text stays crisp at every width. All
   drawing uses currentColor, set once on the SVG from the gauge-fill token. */
.ii-gp-widget__forecast-canvas { position: relative; }
/* Axis labels laid over the plot's left edge, on a translucent chip of the card colour so
   they stay legible on top of the area fill. */
.ii-gp-widget__forecast-axis {
  position: absolute;
  left: 6px;
  padding: 1px 5px;
  border-radius: 4px;
  font-size: var(--gp-fs-eyebrow);
  line-height: 1.3;
  font-variant-numeric: tabular-nums;
  color: var(--gp-prediction-gauge-percentage, #003652);
  background: var(--gp-realtime-bg, #fff);
  background: color-mix(in srgb, var(--gp-realtime-bg, #fff) 72%, transparent);
  pointer-events: none;
}
.ii-gp-widget__forecast-axis--top { top: 3px; }
.ii-gp-widget__forecast-axis--bottom { bottom: 3px; }
.ii-gp-widget__forecast-svg {
  display: block;
  width: 100%;
  height: var(--gp-h-gauge);
  flex: 1 1 var(--gp-h-gauge);
  min-height: 0;
  overflow: visible;
}
/* Grid in the text colour, not the curve colour: the curve carries its own level gradient. */
.ii-gp-widget__forecast-grid { stroke: var(--gp-prediction-gauge-percentage, #003652); stroke-width: 1; stroke-opacity: 0.28; stroke-dasharray: 2 3; }
.ii-gp-widget__forecast-grid--mid { stroke-opacity: 0.14; }
/* fill/stroke come from the per-instance gradient set as attributes in the markup — no CSS
   fill/stroke here, or it would override them. */
.ii-gp-widget__forecast-area { fill-opacity: 0.18; }
.ii-gp-widget__forecast-line { fill: none; stroke-width: 2; stroke-linejoin: round; stroke-linecap: round; }
.ii-gp-widget__forecast-marker { stroke: var(--gp-prediction-gauge-percentage, #003652); stroke-width: 1; stroke-opacity: 0.55; stroke-dasharray: 2 2; }
.ii-gp-widget__forecast-canvas--week .ii-gp-widget__forecast-svg { height: var(--gp-h-chart); flex-basis: var(--gp-h-chart); }
.ii-gp-widget__forecast-canvas--week { cursor: crosshair; touch-action: pan-y; }
.ii-gp-widget__forecast-hours { display: flex; margin-top: 6px; }
.ii-gp-widget__forecast-hours > * {
  flex: 1;
  min-width: 0;
  padding: 0;
  border: none;
  background: none;
  font-family: inherit;
  font-size: var(--gp-fs-eyebrow);
  font-weight: var(--gp-font-weight-bold, 700);
  font-variant-numeric: tabular-nums;
  text-align: center;
  color: var(--gp-prediction-gauge-percentage, #003652);
  opacity: 0.65;
}
.ii-gp-widget__forecast-hour--outside { opacity: 0.35 !important; }
/* Week scrubber: every hour is a target, every third one labelled; the selected hour reads at
   full strength and carries a dot where its label would otherwise be blank. */
.ii-gp-widget__forecast-hours--scrub > * { min-height: 16px; cursor: pointer; border-radius: 4px; }
.ii-gp-widget__forecast-hours--scrub > *:hover { opacity: 1; }
.ii-gp-widget__forecast-hour--selected { opacity: 1 !important; font-weight: var(--gp-font-weight-bold, 700); }
.ii-gp-widget__forecast-hour--selected:empty::after { content: "•"; }
.ii-gp-widget__week-body { padding: 10px var(--gp-sp-panel) var(--gp-sp-panel); background: var(--gp-chart-bg, #fff); }
/* Two columns: the hour and its total on the left, at the same size as the category counts
   on the Now tab; the split into categories stacked on the right. */
.ii-gp-widget__breakdown { display: flex; justify-content: space-between; align-items: center; gap: 16px; margin-top: 12px; color: var(--gp-realtime-small-text, #333); }
/* Centred in its half, both ways, so it doesn't sit in the corner of the space it owns. */
.ii-gp-widget__breakdown-summary { flex: 1 1 0; min-width: 0; text-align: center; }
.ii-gp-widget__breakdown-hour { font-size: var(--gp-fs-body); color: var(--gp-realtime-category-text, #666); margin-bottom: 2px; }
.ii-gp-widget__breakdown-total { display: flex; justify-content: center; align-items: baseline; gap: 5px; }
.ii-gp-widget__breakdown-count { font-size: var(--gp-fs-box-count); font-weight: var(--gp-font-weight-bold, 700); line-height: 1.1; letter-spacing: -0.01em; font-variant-numeric: tabular-nums; color: var(--gp-realtime-big-text, #003652); }
.ii-gp-widget__breakdown-unit { font-size: var(--gp-fs-body); color: var(--gp-realtime-category-text, #666); }
.ii-gp-widget__breakdown-rows { display: flex; flex-direction: column; gap: 3px; flex: 0 1 auto; min-width: 50%; font-size: calc(var(--gp-fs-body) - 1px); line-height: 1.5; }
.ii-gp-widget__breakdown-row { display: flex; justify-content: space-between; gap: 10px; font-variant-numeric: tabular-nums; color: var(--gp-realtime-category-text, #666); }
.ii-gp-widget__breakdown-row span:first-child { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ii-gp-widget__breakdown-row span:last-child { flex-shrink: 0; font-weight: var(--gp-font-weight-bold, 700); color: var(--gp-realtime-big-text, #003652); }
/* Wide: the categories stand side by side as boxes (count over name, the Now tab's sizes) so
   the week panel ends at the same height as the Now panel. */
@container (min-width: 520px) {
  .ii-gp-widget__breakdown { gap: 0; align-items: stretch; }
  .ii-gp-widget__breakdown-summary { flex: 0 0 auto; min-width: 130px; display: flex; flex-direction: column; justify-content: center; }
  .ii-gp-widget__breakdown-rows { flex: 1 1 auto; flex-direction: row; gap: 0; min-width: 0; }
  .ii-gp-widget__breakdown-row { flex: 1 1 0; min-width: 0; flex-direction: column-reverse; align-items: center; gap: 5px; padding: var(--gp-sp-box); text-align: center; }
  .ii-gp-widget__breakdown-row span:first-child { font-size: var(--gp-fs-box-label); line-height: 1.3; max-width: 100%; }
  .ii-gp-widget__breakdown-row span:last-child { font-size: var(--gp-fs-box-count); line-height: 1.1; letter-spacing: -0.01em; }
}

/* ── Week tab ── */

.ii-gp-widget__weekdays {
  display: flex;
  gap: 3px;
  padding: 6px 10px;
  /* The hero's band colour, so the row reads as the panel's header strip like the count does
     on the Now tab, not as the plot's time axis. Text tokens follow: the hero's text for the
     days, the tabs' white-pill pattern for the selected one. */
  background: var(--gp-realtime-total-bg, #1780B5);
}
.ii-gp-widget__weekday {
  flex: 1;
  min-width: 0;
  padding: 6px 2px;
  border: none;
  border-radius: var(--gp-r-ctl);
  background: none;
  font-family: inherit;
  font-size: var(--gp-fs-eyebrow);
  letter-spacing: 0.04em;
  text-transform: var(--gp-text-transform, uppercase);
  color: var(--gp-realtime-total-text, #fff);
  opacity: 0.85;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, opacity 0.15s ease;
}
.ii-gp-widget__weekday:hover:not(.ii-gp-widget__weekday--active) {
  opacity: 1;
  background: color-mix(in srgb, var(--gp-realtime-total-text, #fff) 12%, transparent);
}
.ii-gp-widget__weekday--active {
  opacity: 1;
  background: var(--gp-weekdayrow-active-bg, #fff);
  color: var(--gp-weekdayrow-active-text, #1780B5);
  font-weight: var(--gp-font-weight-bold, 700);
}

/* ── Chain panel ── */

.ii-gp-widget__chain {
  background: var(--gp-realtime-bg, #fff);
  border-top: 1px solid var(--gp-realtime-inner-border, #E7E7E7);
  border-top-color: color-mix(in srgb, var(--gp-realtime-inner-border, #E7E7E7) 75%, var(--gp-realtime-bg, #fff));
  padding: 4px 6px 6px;
}
.ii-gp-widget__chain-title {
  margin: 0;
  padding: 8px 6px 6px;
  font-size: var(--gp-fs-eyebrow);
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--gp-realtime-category-text, #666);
  opacity: 0.8;
}
.ii-gp-widget__chain-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px;
  border-radius: var(--gp-r-ctl);
  font-size: var(--gp-fs-body);
  color: inherit;
  text-decoration: none;
  transition: background 0.15s ease;
}
.ii-gp-widget__chain-row:hover {
  background: color-mix(in srgb, var(--gp-availability-border, #1780B5) 8%, transparent);
}
.ii-gp-widget__chain-count {
  font-weight: var(--gp-font-weight-bold, 700);
  font-variant-numeric: tabular-nums;
  color: var(--gp-realtime-big-text, #003652);
  flex-shrink: 0;
}

/* ── Footer, errors, disclaimers ── */

.ii-gp-widget__footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  padding: 8px var(--gp-sp-panel);
  color: var(--gp-realtime-big-text, #003652);
}
/* Pushed right so it sits just left of the wordmark, and kept faint: a reference, not an
   invitation. */
.ii-gp-widget__privacy {
  margin-left: auto;
  font-size: var(--gp-fs-eyebrow);
  color: inherit;
  text-decoration: none;
  opacity: 0.55;
  transition: opacity 0.15s ease;
}
.ii-gp-widget__privacy:hover { opacity: 0.85; text-decoration: underline; }
/* ── Help: the "i" toggles help mode, in which each control marked with data-hint grows a
      speech bubble (::after) and its tail (::before). Pseudo-elements, so they live on the
      containers and survive the panels' innerHTML re-renders. ── */
.ii-gp-widget__help {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  padding: 0;
  border: 1.5px solid currentColor;
  border-radius: 50%;
  background: none;
  color: inherit;
  font-family: Georgia, "Times New Roman", serif;
  font-size: 12px;
  font-style: italic;
  font-weight: 700;
  line-height: 1;
  cursor: pointer;
  opacity: 0.55;
  /* The wordmark's box includes the descender of its "y", so its letters sit above the box
     centre; lift the icon to the letters rather than the box. */
  transform: translateY(-2px);
  transition: opacity 0.15s ease;
}
.ii-gp-widget__help:hover, .ii-gp-widget__help[aria-pressed="true"] { opacity: 1; }
.ii-gp-widget [data-hint] { position: relative; }
.ii-gp-widget [data-hint]::after {
  content: attr(data-hint);
  position: absolute;
  z-index: 4;
  display: none;
  width: max-content;
  max-width: min(250px, 85%);
  padding: 7px 11px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 9px;
  /* Fixed white, not the theme: the bubbles are the widget's own voice, the same everywhere. */
  background: #fff;
  color: #222;
  font-size: var(--gp-fs-body);
  font-weight: 400;
  line-height: 1.35;
  letter-spacing: 0;
  text-align: left;
  text-transform: none;
  white-space: normal;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.2);
  pointer-events: none;
}
.ii-gp-widget [data-hint]::before {
  content: "";
  position: absolute;
  z-index: 4;
  display: none;
  width: 0;
  height: 0;
  border: 7px solid transparent;
  pointer-events: none;
}
.ii-gp-widget--help [data-hint]::after,
.ii-gp-widget--help [data-hint]::before { display: block; animation: ii-gp-widget-hint-in 0.18s ease-out both; }
@keyframes ii-gp-widget-hint-in { from { opacity: 0; } }
.ii-gp-widget [data-hint-at="below-start"]::after { top: calc(100% + 8px); left: 10px; }
.ii-gp-widget [data-hint-at="below-start"]::before { top: calc(100% - 6px); left: 22px; border-bottom-color: #fff; }
/* On what they describe, no tail: along the top edge of the Now plot, dead centre of the
   week plot and of the category grid. */
.ii-gp-widget [data-hint-at="inside-top"]::after { top: 10px; left: 50%; transform: translateX(-50%); }
.ii-gp-widget [data-hint-at="center"]::after { top: 50%; left: 50%; transform: translate(-50%, -50%); }
.ii-gp-widget--help [data-hint-at="inside-top"]::before,
.ii-gp-widget--help [data-hint-at="center"]::before { display: none; }
/* Just under a vertically centred control (the plan button, ~36px tall), tail pointing up at
   it, so the control itself stays uncovered. */
.ii-gp-widget [data-hint-at="below-center"]::after { top: calc(50% + 26px); left: 50%; transform: translateX(-50%); }
.ii-gp-widget [data-hint-at="below-center"]::before { top: calc(50% + 12px); left: 50%; transform: translateX(-50%); border-bottom-color: #fff; }
.ii-gp-widget__footer-link { display: block; line-height: 0; color: inherit; text-decoration: none; opacity: 0.9; transition: opacity 0.15s ease; }
.ii-gp-widget__footer-link:hover { opacity: 1; }
.ii-gp-widget__logo { height: var(--gp-h-logo); width: auto; display: block; }
/* The Now tab's shape while it waits for numbers. Sizes come from the same variables the real
   parts use, so the panel is already the height it will keep and nothing shifts when the data
   lands. Blocks are a wash of the panel's own text colour rather than a grey, so the skeleton
   belongs to whatever theme the embed asked for. */
.ii-gp-widget__skeleton {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: var(--gp-sp-panel);
}
.ii-gp-widget__skeleton-hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  /* The same padding and count size the real hero uses, so the band is exactly as tall. */
  padding: 14px var(--gp-sp-panel);
  background: var(--gp-realtime-bg, #EEF3F6);
  border-radius: var(--gp-border-radius, 6px);
}
.ii-gp-widget__skeleton-bar,
.ii-gp-widget__skeleton-box,
.ii-gp-widget__skeleton-curve {
  background: color-mix(in srgb, var(--gp-content-text, #3A3A3A) 12%, transparent);
  border-radius: var(--gp-border-radius, 6px);
}
.ii-gp-widget__skeleton-bar { height: 16px; }
.ii-gp-widget__skeleton-bar--label { width: 42%; }
.ii-gp-widget__skeleton-bar--count { width: 22%; height: var(--gp-fs-total-count, 30px); }
.ii-gp-widget__skeleton-bar--plot-title { width: 46%; height: 13px; align-self: center; }
.ii-gp-widget__skeleton-boxes {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
}
.ii-gp-widget__skeleton-box { height: 62px; }
/* The feedback half of the foot: centred like the real block, and tighter, because its three
   rows are text and stars rather than panels. */
.ii-gp-widget__skeleton--feedback { align-items: center; gap: 10px; }
.ii-gp-widget__skeleton-bar--title { width: 32%; height: 14px; }
.ii-gp-widget__skeleton-bar--question { width: 72%; height: 14px; }
.ii-gp-widget__skeleton-stars { display: flex; gap: 5px; }
.ii-gp-widget__skeleton-star {
  width: var(--gp-fs-star, 18px);
  height: var(--gp-fs-star, 18px);
  border-radius: 50%;
  background: color-mix(in srgb, var(--gp-content-text, #3A3A3A) 12%, transparent);
}
.ii-gp-widget__skeleton-curve { height: var(--gp-h-gauge, 72px); }
@container (min-width: 520px) {
  .ii-gp-widget__skeleton-boxes { grid-template-columns: repeat(4, 1fr); }
}
/* One slow breath across the whole group, not a shimmer sweeping each block: a widget sitting
   in someone's page should not be the busiest thing on it. */
.ii-gp-widget__skeleton { animation: ii-gp-skeleton-pulse 1.6s ease-in-out infinite; }
@keyframes ii-gp-skeleton-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.55; } }
@media (prefers-reduced-motion: reduce) {
  .ii-gp-widget__skeleton { animation: none; }
}

/* A failed load is the widget's only empty state, and it fills the panel a visitor came to
   read, so it is composed rather than reported: the glyph carries the bad news and the words
   stay level. The red is kept for the mark alone; the text uses the theme's own colours, so a
   dark embed does not get a light-mode warning pasted into it. */
.ii-gp-widget__error {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 6px;
  padding: 22px var(--gp-sp-panel) 24px;
  font-size: var(--gp-fs-body);
  line-height: 1.5;
}
.ii-gp-widget__error-icon {
  width: 30px;
  height: 30px;
  margin-bottom: 2px;
  /* Muted rather than alarm red: nothing is broken for the visitor, the data is just late. */
  color: color-mix(in srgb, #d0483b 70%, var(--gp-content-text, #3A3A3A));
}
.ii-gp-widget__error-title {
  margin: 0;
  font-weight: var(--gp-font-weight-bold, 700);
  color: var(--gp-realtime-big-text, #003652);
}
.ii-gp-widget__error-note {
  margin: 0;
  max-width: 34ch;
  color: var(--gp-content-text, #3A3A3A);
  opacity: 0.75;
}
/* The same button the plan uses: filled, no border. --gp-border-width is 6px on the bold
   thicknesses, which is a frame around a panel, not around a control. */
.ii-gp-widget__error-retry {
  margin-top: 8px;
  padding: 9px 20px;
  font-family: inherit;
  font-size: var(--gp-fs-body);
  font-weight: var(--gp-font-weight-bold, 700);
  color: var(--gp-tab-active-text, #fff);
  background: var(--gp-tab-active-bg, #1780B5);
  border: none;
  border-radius: var(--gp-r-ctl);
  cursor: pointer;
}
.ii-gp-widget__error-retry:hover { opacity: 0.9; }

/* Everything outside the Now panel is built from the render payload: the week curve is drawn
   from it, the plan needs the premise's endpoint out of it, the feedback widget is mounted with
   its id. With no payload they are controls with nothing behind them, so a failed load takes
   them away rather than leaving them to be pressed. */
.ii-gp-widget--failed .ii-gp-widget__tabs,
.ii-gp-widget--failed .ii-gp-widget__foot { display: none; }
/* While loading they stay in place, because hiding and restoring them would move the page the
   widget is sitting in. They just cannot be used yet. */
.ii-gp-widget--loading .ii-gp-widget__tab,
.ii-gp-widget--loading .ii-gp-widget__aipt-open {
  pointer-events: none;
  opacity: 0.55;
}
.ii-gp-widget__disclaimer {
  padding: 6px var(--gp-sp-panel);
  font-size: var(--gp-fs-eyebrow);
  line-height: 1.4;
  color: var(--gp-realtime-category-text, #888);
  opacity: 0.8;
}

/* ── AI-PT: a button between the curve and the survey that opens the flow in place ── */

/* On a light tint of the tab-bar colour so the section reads as its own thing, apart from
   the counter above it. The tint does the separating, so no hairline. */
.ii-gp-widget__aipt {
  container: ii-gp-aipt / inline-size;
  background: var(--gp-tab-bg, #E7E7E7);
  background: color-mix(in srgb, var(--gp-tab-bg, #E7E7E7) 60%, var(--gp-realtime-bg, #fff));
}
/* Button and figures as one centred group: the figures stand over the button's right end,
   the button vertically centred on them — the button pads its right side by the overlap so
   the label stays clear. */
.ii-gp-widget__aipt-cta {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: var(--gp-sp-panel);
}
/* The trainer figures stand together at the right end of the row, feet on one line, the
   woman a step behind the man. Height-bound so the row stays one line tall; the files are
   240px high, so still crisp at 2× of the largest tier. */
.ii-gp-widget__aipt-figures {
  display: flex;
  align-items: flex-end;
  position: relative;
  z-index: 1;
  margin-left: calc(var(--gp-h-figure) * -0.55);
}
.ii-gp-widget__aipt-figure { height: var(--gp-h-figure); width: auto; }
.ii-gp-widget__aipt-figure--back { height: calc(var(--gp-h-figure) * 0.88); }
.ii-gp-widget__aipt-figure--front { position: relative; z-index: 1; margin-left: calc(var(--gp-h-figure) * -0.3); }
/* The button carries both labels; when the section is under 400px wide the shorter one is
   shown so the label stays on one line next to the figures. Measured on the section, not the
   widget: beside the feedback panel it has half the width. */
.ii-gp-widget__aipt-open-short { display: none; }
@container ii-gp-aipt (max-width: 399px) {
  .ii-gp-widget__aipt-open-long { display: none; }
  .ii-gp-widget__aipt-open-short { display: inline; }
  .ii-gp-widget__aipt-cta { --gp-h-figure: 56px; }
  .ii-gp-widget__aipt-open { padding: 10px calc(12px + var(--gp-h-figure) * 0.55); }
}
.ii-gp-widget__aipt-open {
  text-align: center;
  line-height: 1.25;
  /* Equal on both sides, each widened by the figure overlap: the label sits at the button's
     true centre and stays clear of the figures on the right. */
  padding: 10px calc(18px + var(--gp-h-figure) * 0.55);
  border: none;
  border-radius: var(--gp-r-ctl);
  background: var(--gp-tab-active-bg, #1780B5);
  color: var(--gp-tab-active-text, #fff);
  font-family: inherit;
  font-size: var(--gp-fs-body);
  font-weight: var(--gp-font-weight-bold, 700);
  cursor: pointer;
  transition: filter 0.15s ease;
}
.ii-gp-widget__aipt-open:hover { filter: brightness(1.08); }
/* Covers .ii-gp-widget__main at its current size — inset:0 against the positioned parent
   above, not a height of its own — so opening AI-PT never changes the widget's footprint.
   .ii-gp-widget__aipt-head stays put and .ii-gp-widget__aipt-body scrolls internally instead
   of growing the card once the form gives way to a loader or a full plan. */
.ii-gp-widget__aipt-overlay {
  position: absolute;
  inset: 0;
  z-index: 1;
  display: flex;
  flex-direction: column;
  background: var(--gp-realtime-bg, #fff);
  color: var(--gp-realtime-small-text, #333);
  animation: ii-gp-aipt-overlay-in 0.15s ease;
}
@keyframes ii-gp-aipt-overlay-in {
  from { opacity: 0; }
  to { opacity: 1; }
}
.ii-gp-widget__aipt-head {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px var(--gp-sp-panel);
  border-bottom: 1px solid var(--gp-realtime-inner-border, #E7E7E7);
  border-bottom-color: color-mix(in srgb, var(--gp-realtime-inner-border, #E7E7E7) 75%, var(--gp-realtime-bg, #fff));
  font-size: var(--gp-fs-body);
  font-weight: var(--gp-font-weight-bold, 700);
  color: var(--gp-realtime-big-text, #003652);
}
.ii-gp-widget__aipt-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}
.ii-gp-widget__aipt-close {
  width: 28px;
  height: 28px;
  border: none;
  border-radius: var(--gp-r-ctl);
  background: none;
  font-size: 18px;
  line-height: 1;
  color: var(--gp-realtime-category-text, #666);
  cursor: pointer;
}
.ii-gp-widget__aipt-close:hover { background: color-mix(in srgb, var(--gp-realtime-category-text, #666) 10%, transparent); }

/* ── data-type="onlytotal": the compact bar (live total + "see more") that replaces the
      regular embed, and the modal it opens. The modal is a second, ordinary
      .ii-gp-widget-host > .ii-gp-widget pair — built and styled exactly like a normal embed,
      just parented under .ii-gp-widget-modal-backdrop instead of the page's own flow (see
      index.ts for why it has to live outside the compact bar's own host: container-type on
      .ii-gp-widget-host makes it a containing block for position:fixed descendants, which
      would otherwise trap a fixed-position modal inside the small compact box). ── */
.ii-gp-widget__compact { display: flex; flex-direction: column; }
.ii-gp-widget__compact-more {
  display: block;
  width: 100%;
  padding: 8px var(--gp-sp-panel);
  border: none;
  background: none;
  font-family: inherit;
  font-size: var(--gp-fs-eyebrow);
  font-weight: var(--gp-font-weight-bold, 700);
  color: var(--gp-tab-text, #1780B5);
  text-align: center;
  text-decoration: underline;
  cursor: pointer;
}
.ii-gp-widget__compact-more:hover { opacity: 0.8; }

.ii-gp-widget-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 2147483000;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  overflow-y: auto;
  padding: 24px 16px;
  background: rgba(0, 0, 0, 0.55);
  animation: ii-gp-modal-backdrop-in 0.15s ease;
}
.ii-gp-widget-modal-backdrop[hidden] { display: none; }
@keyframes ii-gp-modal-backdrop-in {
  from { opacity: 0; }
  to { opacity: 1; }
}
/* As a flex item of the backdrop, the modal's .ii-gp-widget-host doesn't get the
   width-filling it has as a block in normal flow — and container-type: inline-size zeroes its
   intrinsic width, so it would collapse to its 260px min-width floor. width: 100% restores the
   fill, so its max-width cap (--gp-max-width) applies exactly as in the regular embed. */
.ii-gp-widget-modal-backdrop .ii-gp-widget-host { width: 100%; margin: auto 0; }
.ii-gp-widget-modal-backdrop .ii-gp-widget { position: relative; }
.ii-gp-widget__full-close {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 2;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: var(--gp-r-ctl);
  background: color-mix(in srgb, var(--gp-realtime-bg, #fff) 70%, transparent);
  font-size: 18px;
  line-height: 1;
  color: var(--gp-realtime-category-text, #666);
  cursor: pointer;
}
/* AI-PT's overlay has its own × in the same top-right corner; while it is open that one is the
   only close shown, so a tap on it can't land on the modal's instead. */
.ii-gp-widget--aipt-open .ii-gp-widget__full-close { display: none; }
.ii-gp-widget__full-close:hover { background: color-mix(in srgb, var(--gp-realtime-category-text, #666) 15%, var(--gp-realtime-bg, #fff)); }

.ii-gp-widget .ii-gp-aipt__form {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: var(--gp-sp-panel);
}
.ii-gp-widget .ii-gp-aipt__description {
  margin: 0;
  font-size: var(--gp-fs-body);
  line-height: 1.45;
  color: var(--gp-realtime-big-text, #003652);
}
.ii-gp-widget .ii-gp-aipt__form fieldset {
  margin: 0;
  padding: 10px 12px 12px;
  border: 1px solid var(--gp-realtime-inner-border, #E7E7E7);
  border-color: color-mix(in srgb, var(--gp-realtime-inner-border, #E7E7E7) 85%, var(--gp-realtime-bg, #fff));
  border-radius: var(--gp-r-ctl);
}
.ii-gp-widget .ii-gp-aipt__form legend {
  padding: 0 4px;
  font-size: var(--gp-fs-body);
  font-weight: var(--gp-font-weight-bold, 700);
  color: var(--gp-realtime-big-text, #003652);
}
.ii-gp-widget .ii-gp-aipt__option {
  display: flex;
  color: var(--gp-realtime-small-text, #333);
  align-items: center;
  gap: 8px;
  padding: 4px 0;
  font-size: var(--gp-fs-body);
  line-height: 1.4;
  cursor: pointer;
}
.ii-gp-widget .ii-gp-aipt__option input { accent-color: var(--gp-tab-active-bg, #1780B5); margin: 0; }
.ii-gp-widget .ii-gp-aipt__submit {
  padding: 11px;
  border: none;
  border-radius: var(--gp-r-ctl);
  background: var(--gp-tab-active-bg, #1780B5);
  color: var(--gp-tab-active-text, #fff);
  font-family: inherit;
  font-size: var(--gp-fs-body);
  font-weight: var(--gp-font-weight-bold, 700);
  cursor: pointer;
  transition: filter 0.15s ease;
}
.ii-gp-widget .ii-gp-aipt__submit:hover { filter: brightness(1.08); }
.ii-gp-widget .ii-gp-aipt__disclaimer {
  margin: 0;
  font-size: var(--gp-fs-eyebrow);
  line-height: 1.45;
  color: var(--gp-realtime-category-text, #888);
  opacity: 0.8;
}
.ii-gp-widget .ii-gp-aipt__loader { padding: 32px var(--gp-sp-panel); text-align: center; }
.ii-gp-widget .ii-gp-aipt__loader-header {
  margin: 0 0 8px;
  font-size: var(--gp-fs-body);
  font-weight: var(--gp-font-weight-bold, 700);
  letter-spacing: 0.05em;
  text-transform: var(--gp-text-transform, uppercase);
  color: var(--gp-realtime-big-text, #003652);
}
.ii-gp-widget .ii-gp-aipt__loader-message {
  margin: 0;
  font-size: var(--gp-fs-body);
  color: var(--gp-realtime-category-text, #666);
  /* Cross-fades as the message rotates, instead of snapping between strings. */
  animation: ii-gp-aipt-fade 4s ease-in-out infinite;
}
@keyframes ii-gp-aipt-fade { 0%, 100% { opacity: 0.55; } 25%, 75% { opacity: 1; } }
.ii-gp-widget .ii-gp-aipt__answer { padding: var(--gp-sp-panel); }
.ii-gp-widget .ii-gp-aipt__answer-title {
  margin: 0 0 10px;
  font-size: calc(15px * var(--gp-font-scale));
  font-weight: var(--gp-font-weight-bold, 700);
  color: var(--gp-realtime-big-text, #003652);
}
.ii-gp-widget .ii-gp-aipt__answer-plan { margin: 0; color: var(--gp-realtime-small-text, #333); font-size: var(--gp-fs-body); line-height: 1.6; }
.ii-gp-widget .ii-gp-aipt__pdf {
  display: inline-block;
  margin: 12px 0 0;
  font-size: var(--gp-fs-body);
  font-weight: var(--gp-font-weight-bold, 700);
  color: var(--gp-availability-border, #1780B5);
}
.ii-gp-widget .ii-gp-aipt__error {
  padding: 18px var(--gp-sp-panel);
  font-size: var(--gp-fs-body);
  line-height: 1.5;
  color: #b00020;
}
.ii-gp-widget .ii-gp-aipt__email { margin-top: 14px; }
.ii-gp-widget .ii-gp-aipt__email-label { display: block; margin-bottom: 6px; font-size: var(--gp-fs-body); font-weight: var(--gp-font-weight-bold, 700); color: var(--gp-realtime-big-text, #003652); }
.ii-gp-widget .ii-gp-aipt__email-row { display: flex; gap: 6px; }
.ii-gp-widget .ii-gp-aipt__email-input {
  flex: 1;
  min-width: 0;
  padding: 9px 10px;
  border: 1px solid var(--gp-realtime-inner-border, #E7E7E7);
  border-color: color-mix(in srgb, var(--gp-availability-border, #1780B5) 25%, transparent);
  border-radius: var(--gp-r-ctl);
  background: var(--gp-realtime-bg, #fff);
  color: inherit;
  font-family: inherit;
  font-size: var(--gp-fs-body);
  transition: border-color 0.15s ease;
}
.ii-gp-widget .ii-gp-aipt__email-input:focus { outline: none; border-color: var(--gp-availability-border, #1780B5); }
.ii-gp-widget .ii-gp-aipt__email-send {
  flex: 0 0 auto;
  padding: 9px 14px;
  border: none;
  border-radius: var(--gp-r-ctl);
  background: var(--gp-tab-active-bg, #1780B5);
  color: var(--gp-tab-active-text, #fff);
  font-family: inherit;
  font-size: var(--gp-fs-body);
  font-weight: var(--gp-font-weight-bold, 700);
  cursor: pointer;
  transition: filter 0.15s ease;
}
.ii-gp-widget .ii-gp-aipt__email-send:hover:not(:disabled) { filter: brightness(1.08); }
.ii-gp-widget .ii-gp-aipt__email-send:disabled, .ii-gp-widget .ii-gp-aipt__email-input:disabled { opacity: 0.55; cursor: default; }
.ii-gp-widget .ii-gp-aipt__email--sent .ii-gp-aipt__email-row { display: none; }
.ii-gp-widget .ii-gp-aipt__email-consent { display: flex; align-items: flex-start; gap: 7px; margin-top: 8px; font-size: var(--gp-fs-eyebrow); line-height: 1.45; color: var(--gp-realtime-category-text, #666); cursor: pointer; }
.ii-gp-widget .ii-gp-aipt__email-consent-box { flex-shrink: 0; margin: 1px 0 0; accent-color: var(--gp-tab-active-bg, #1780B5); cursor: pointer; }
.ii-gp-widget .ii-gp-aipt__email-consent-box:disabled { cursor: default; }
/* Indented past the checkbox so it reads as a note on the choice above it, not as a second
   thing to agree to. Quieter than the label for the same reason. */
.ii-gp-widget .ii-gp-aipt__email-consent-note { margin: 3px 0 0 20px; font-size: var(--gp-fs-eyebrow); line-height: 1.45; color: var(--gp-realtime-category-text, #666); opacity: 0.75; }
.ii-gp-widget .ii-gp-aipt__email--sent .ii-gp-aipt__email-consent,
.ii-gp-widget .ii-gp-aipt__email--sent .ii-gp-aipt__email-consent-note { display: none; }
.ii-gp-widget .ii-gp-aipt__email-status { margin: 6px 0 0; font-size: var(--gp-fs-eyebrow); line-height: 1.45; color: var(--gp-realtime-category-text, #666); }
.ii-gp-widget .ii-gp-aipt__email-status--error { color: #b00020; }
.ii-gp-widget .ii-gp-aipt__thumbs { display: flex; gap: 8px; margin-top: 14px; }
.ii-gp-widget .ii-gp-aipt__thumb {
  flex: 1;
  padding: 8px;
  border: 1px solid var(--gp-realtime-inner-border, #E7E7E7);
  border-color: color-mix(in srgb, var(--gp-availability-border, #1780B5) 25%, transparent);
  border-radius: var(--gp-r-ctl);
  background: none;
  font-family: inherit;
  font-size: var(--gp-fs-body);
  color: inherit;
  cursor: pointer;
  transition: background 0.15s ease;
}
.ii-gp-widget .ii-gp-aipt__thumb:hover:not(:disabled) {
  background: color-mix(in srgb, var(--gp-availability-border, #1780B5) 8%, transparent);
}
.ii-gp-widget .ii-gp-aipt__thumb:disabled { opacity: 0.45; cursor: default; }
.ii-gp-widget .ii-gp-aipt__thanks {
  margin: 8px 0 0;
  font-size: var(--gp-fs-eyebrow);
  color: var(--gp-realtime-category-text, #666);
}

/* ── Feedback ── */

.ii-gp-widget__feedback:empty { display: none; }
@container (min-width: 520px) {
  .ii-gp-widget__foot { display: flex; align-items: stretch; }
  .ii-gp-widget__foot > .ii-gp-widget__aipt { flex: 1 1 50%; min-width: 0; }
  /* Side by side the plan section drops its tint and a hairline separates the two; alone
     (form open, feedback hidden) it keeps the tint. */
  .ii-gp-widget__foot:has(> .ii-gp-widget__feedback:not([hidden])) > .ii-gp-widget__aipt { background: var(--gp-realtime-bg, #fff); }
  .ii-gp-widget__foot > .ii-gp-widget__feedback {
    flex: 1 1 50%;
    min-width: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
  .ii-gp-widget__foot > .ii-gp-widget__aipt:not([hidden]) + .ii-gp-widget__feedback {
    border-left: 1px solid var(--gp-realtime-inner-border, #E7E7E7);
    border-left-color: color-mix(in srgb, var(--gp-realtime-inner-border, #E7E7E7) 75%, var(--gp-realtime-bg, #fff));
  }
  .ii-gp-widget__foot .ii-gp-feedback__rating,
  .ii-gp-widget__foot .ii-gp-feedback__freeform { border-top: none; }
}
.ii-gp-feedback__rating, .ii-gp-feedback__freeform {
  padding: var(--gp-sp-panel);
  text-align: center;
  border-top: 1px solid var(--gp-realtime-inner-border, #E7E7E7);
  border-top-color: color-mix(in srgb, var(--gp-realtime-inner-border, #E7E7E7) 75%, var(--gp-realtime-bg, #fff));
}
.ii-gp-feedback__title {
  margin: 0 0 6px;
  font-size: var(--gp-fs-body);
  font-weight: var(--gp-font-weight-bold, 700);
  color: var(--gp-realtime-big-text, #003652);
}
.ii-gp-feedback__question {
  margin: 6px 0 0;
  font-size: var(--gp-fs-body);
  line-height: 1.45;
  color: var(--gp-realtime-big-text, #003652);
}
.ii-gp-feedback__stars { display: flex; justify-content: center; }
.ii-gp-feedback__star {
  padding: 2px 4px;
  border: none;
  background: none;
  font-size: var(--gp-fs-star);
  line-height: 1;
  /* The text token, not the border token: the border token is a hairline grey in the neutral
     presets and would make the stars nearly invisible. */
  color: var(--gp-realtime-big-text, #003652);
  /* Faint enough to read as "not yet rated" without pulling the eye from the data above. */
  opacity: 0.3;
  cursor: pointer;
  transition: opacity 0.15s ease, transform 0.15s ease;
}
/* Hovering star N fills 1..N: the whole row lights up, then the stars after the hovered one
   are dimmed again. No gap between the buttons, so there is no dead zone between stars. */
.ii-gp-feedback__stars:hover .ii-gp-feedback__star { opacity: 1; }
.ii-gp-feedback__star:hover ~ .ii-gp-feedback__star { opacity: 0.3; }
.ii-gp-feedback__star:hover { transform: scale(1.15); }
.ii-gp-feedback__textarea {
  width: 100%;
  padding: 9px 10px;
  border: 1px solid var(--gp-realtime-inner-border, #E7E7E7);
  border-color: color-mix(in srgb, var(--gp-availability-border, #1780B5) 25%, transparent);
  border-radius: var(--gp-r-ctl);
  background: var(--gp-realtime-bg, #fff);
  color: inherit;
  font-family: inherit;
  font-size: var(--gp-fs-body);
  line-height: 1.45;
  resize: vertical;
  transition: border-color 0.15s ease;
}
.ii-gp-feedback__textarea:focus {
  outline: none;
  border-color: var(--gp-availability-border, #1780B5);
}
.ii-gp-feedback__actions { display: flex; justify-content: center; gap: 8px; margin-top: 10px; }
.ii-gp-feedback__actions button {
  padding: 8px 14px;
  border: none;
  border-radius: var(--gp-r-ctl);
  font-family: inherit;
  font-size: var(--gp-fs-body);
  cursor: pointer;
  transition: background 0.15s ease, filter 0.15s ease;
}
.ii-gp-feedback__skip { background: none; color: var(--gp-realtime-category-text, #666); }
.ii-gp-feedback__skip:hover {
  background: color-mix(in srgb, var(--gp-realtime-category-text, #666) 10%, transparent);
}
.ii-gp-feedback__submit {
  background: var(--gp-tab-active-bg, #1780B5);
  color: var(--gp-tab-active-text, #fff);
  font-weight: var(--gp-font-weight-bold, 700);
}
.ii-gp-feedback__submit:hover { filter: brightness(1.08); }
.ii-gp-feedback__thanks {
  margin: 0;
  padding: 14px var(--gp-sp-panel);
  font-size: var(--gp-fs-body);
  text-align: center;
  color: var(--gp-realtime-category-text, #666);
  border-top: 1px solid var(--gp-realtime-inner-border, #E7E7E7);
  border-top-color: color-mix(in srgb, var(--gp-realtime-inner-border, #E7E7E7) 75%, var(--gp-realtime-bg, #fff));
}

/* ── Sizing tiers, keyed off the widget's own width rather than the viewport: an embed in a
      300px sidebar has to look narrow even on a 1920px desktop, which a media query would
      get backwards. A browser without @container support gets the regular tier at every
      width. ── */

@container (max-width: 319px) {
  .ii-gp-widget {
    --gp-fs-tab: calc(11px * var(--gp-font-scale));
    --gp-sp-tab: 8px 4px;
    --gp-fs-total-count: calc(25px * var(--gp-font-scale));
    --gp-fs-total-label: calc(10px * var(--gp-font-scale));
    --gp-fs-box-count: calc(18px * var(--gp-font-scale));
    --gp-fs-box-label: calc(10px * var(--gp-font-scale));
    --gp-sp-box: 10px 6px;
    --gp-w-box-min: 96px;
    --gp-fs-eyebrow: calc(9px * var(--gp-font-scale));
    --gp-h-logo: 12px;
    --gp-h-gauge: 54px;
    --gp-h-chart: 96px;
    --gp-fs-body: calc(11px * var(--gp-font-scale));
    --gp-fs-star: calc(16px * var(--gp-font-scale));
    --gp-sp-panel: 10px;
    --gp-h-figure: 56px;
  }
}
/* Wide: text steps up a point and labels stop ellipsizing, but nothing vertical grows — the
   plot, figures and paddings keep the regular tier's sizes, so as the widget widens and its
   parts move side by side, it gets shorter, not taller. */
@container (min-width: 520px) {
  .ii-gp-widget {
    --gp-fs-tab: calc(14px * var(--gp-font-scale));
    --gp-fs-total-label: calc(12px * var(--gp-font-scale));
    --gp-fs-box-label: calc(12px * var(--gp-font-scale));
    --gp-w-box-min: 150px;
    --gp-box-label-wrap: normal;
    --gp-fs-eyebrow: calc(11px * var(--gp-font-scale));
    --gp-h-logo: 16px;
    --gp-fs-body: calc(13px * var(--gp-font-scale));
    /* Same height as the Now plot: the two tabs share one cell, and with the week's breakdown
       now a single row the plot is the only thing left making the week panel taller. */
    --gp-h-chart: var(--gp-h-gauge);
  }
}

@media (prefers-reduced-motion: reduce) {
  .ii-gp-widget__live-dot, .ii-gp-widget .ii-gp-aipt__loader-message { animation: none; }
  .ii-gp-widget *, .ii-gp-widget *::before, .ii-gp-widget *::after { transition: none !important; }
}
`;
